import { type ComponentType } from 'react';
import { Card, Header, PageHead, Stub, Text, VStack } from '@direct-frontend/components';
import { useLocation, useParams } from '@direct-frontend/modules-router';
import { PRODUCT_ID_NEW } from '#entities/product';
import { PageStub } from '#shared/components/PageStub';
import { resolveProductFormSection } from '../../model/productFormSectionRouting';
import { type ProductFormSection } from '../../model/productFormSections';
import { ProductFormLayout } from '../ProductFormLayout';

const ProductMainSectionStub = () => (
    <>
        <Card
            color="white"
            padding="36"
            paddingCorrection="top-bottom"
            borderRadius="12"
            data-testid="ProductForm.Stub.Main"
        >
            <VStack gap="28">
                <Header level="h2">Название и описание</Header>
                <VStack gap="16">
                    <Stub width="100%" height={44} borderRadius="14" />
                    <Stub width="100%" height={200} borderRadius="14" />
                </VStack>
            </VStack>
        </Card>
        <Card color="white" padding="36" paddingCorrection="top-bottom" borderRadius="12">
            <VStack gap="28">
                <Stub width={180} height={32} borderRadius="8" />
                <Stub width="100%" height={44} borderRadius="14" />
            </VStack>
        </Card>
        <Card color="white" padding="36" paddingCorrection="top-bottom" borderRadius="12">
            <VStack gap="28">
                <Stub width={180} height={32} borderRadius="8" />
                <Stub width="100%" height={200} borderRadius="14" />
            </VStack>
        </Card>
    </>
);

const ProductStocksSectionStub = () => (
    <div data-testid="ProductForm.Stub.Stocks">
        <PageStub />
    </div>
);

const PRODUCT_FORM_SECTION_STUBS = {
    main: ProductMainSectionStub,
    stocks: ProductStocksSectionStub,
} satisfies Record<ProductFormSection, ComponentType>;

interface ProductCardStubViewProps {
    productId: string;
    variantId?: string;
    search: string;
    section: ProductFormSection;
}

export const ProductCardStubView = ({ productId, variantId, search, section }: ProductCardStubViewProps) => {
    const isNew = productId === PRODUCT_ID_NEW;
    const SectionStub = PRODUCT_FORM_SECTION_STUBS[section];

    return (
        <ProductFormLayout
            productId={productId}
            variantId={variantId}
            search={search}
            section={section}
            isNew={isNew}
            isLoading
        >
            <PageHead
                breadcrumbs={isNew ? undefined : [{ text: 'Редактирование товара' }]}
                title={
                    isNew ? (
                        <Text size="xl" bold>
                            Создание товара
                        </Text>
                    ) : (
                        <Stub width="100%" height={32} borderRadius="8" />
                    )
                }
                labels={isNew ? undefined : () => <Stub width={104} height={20} borderRadius="8" />}
                caption={isNew ? undefined : <Stub width={220} height={20} borderRadius="8" />}
            />
            <SectionStub />
        </ProductFormLayout>
    );
};

export const ProductCardStub = () => {
    const location = useLocation();
    const { productId = PRODUCT_ID_NEW, variantId } = useParams<'productId' | 'variantId'>();

    return (
        <ProductCardStubView
            productId={productId}
            variantId={variantId}
            search={location.search}
            section={resolveProductFormSection(location.pathname)}
        />
    );
};
